// ---------------------------------------------------------------------------
// TopicsRail — rail vertical com Topicos e SubTopicos (Variante B).
//
// O Topic ativo se expande automaticamente, mostrando seus SubTopicos.
// Os demais ficam recolhidos. Click no header de outro Topic recolhe o
// anterior e expande o novo. Click em SubTopic do Topic atual emite
// `onSelectSub(slug)` para o parent (que chama scrollTo); click em
// SubTopic de outro Topic emite `onSelectTopicAndSub(topicSlug, subSlug)`.
//
// Filtro por `query` (string vazia = sem filtro). Topics sem subs que
// batem com a busca somem da rail.
//
// Scroll: combina `useOverlayScrollStyles` (scrollbar fina, transparente
// hover-revealed — mesmo padrao da lista de responsaveis em InputsV3)
// com `useNativeWheelScroll` (consome wheel evt deterministicamente
// para nao propagar pro ancestral, que e o caso comum quando o app
// usa PerfectScrollbar no wrapper Main).
// ---------------------------------------------------------------------------

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "@material-ui/icons";
import documentationV3Style from "../../../assets/jss/material-dashboard-pro-react/views/documentationV3Style";
import {
  useOverlayScrollStyles,
  useNativeWheelScroll,
} from "../inputs_v3_components";

const useStyles = makeStyles(documentationV3Style);

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || "";
}

function matchesQuery(text, q) {
  if (!q) return true;
  return text.toLowerCase().includes(q.toLowerCase());
}

function topicMatchesSearch(topic, q) {
  if (!q) return true;
  if (matchesQuery(topic.title, q)) return true;
  return topic.subtopics.some(
    (s) => matchesQuery(s.title, q) || matchesQuery(stripHtml(s.content), q),
  );
}

function subMatchesSearch(s, topic, q) {
  return (
    !q ||
    matchesQuery(s.title, q) ||
    matchesQuery(stripHtml(s.content), q) ||
    matchesQuery(topic.title, q)
  );
}

export default function TopicsRail({
  topics,
  currentTopicSlug,
  scrolledSubSlug,
  query,
  onSelectTopic,
  onSelectSub,
  onSelectTopicAndSub,
  showDrift,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const overlayScrollClasses = useOverlayScrollStyles();
  const wheelRef = useNativeWheelScroll();

  const visibleTopics = useMemo(
    () => topics.filter((t) => topicMatchesSearch(t, query)),
    [topics, query],
  );

  return (
    <nav
      ref={wheelRef}
      className={cx(classes.rail, overlayScrollClasses.overlayScroll)}
      aria-label={t("modeling_documentation.rail_aria_label")}
    >
      {visibleTopics.map((topic) => {
        const isCurrent = topic.slug === currentTopicSlug;
        const visibleSubs = topic.subtopics.filter((s) =>
          subMatchesSearch(s, topic, query),
        );

        return (
          <div key={topic.slug} className={classes.railTopic}>
            <div
              className={cx(classes.railTopicHeader, {
                [classes.railTopicHeaderActive]: isCurrent,
              })}
              onClick={() => onSelectTopic(topic.slug)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectTopic(topic.slug);
                }
              }}
            >
              <ChevronRight
                className={cx(classes.railChevron, {
                  [classes.railChevronExpanded]: isCurrent,
                })}
                fontSize="small"
              />
              <span
                className={cx(classes.railTopicPosition, {
                  [classes.railTopicPositionActive]: isCurrent,
                })}
              >
                {topic.position}
              </span>
              <span className={classes.railTopicTitle}>{topic.title}</span>
              {showDrift && topic.stale_subtopic_count > 0 && (
                <span
                  className={classes.railStaleBadge}
                  title={t("modeling_documentation.rail_stale_count", {
                    count: topic.stale_subtopic_count,
                  })}
                >
                  {topic.stale_subtopic_count}
                </span>
              )}
              {showDrift && topic.manual_lock && (
                <span
                  className={classes.railLockBadge}
                  title={t("modeling_documentation.rail_locked")}
                >
                  L
                </span>
              )}
            </div>

            <ul
              className={cx(classes.railSubs, {
                [classes.railSubsExpanded]: isCurrent,
              })}
            >
              {visibleSubs.map((s, i) => {
                const isActiveSub = isCurrent && s.slug === scrolledSubSlug;
                return (
                  <li
                    key={s.slug}
                    className={cx(classes.railSub, {
                      [classes.railSubActive]: isActiveSub,
                    })}
                    onClick={() => {
                      if (isCurrent) {
                        onSelectSub(s.slug);
                      } else {
                        onSelectTopicAndSub(topic.slug, s.slug);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (isCurrent) onSelectSub(s.slug);
                        else onSelectTopicAndSub(topic.slug, s.slug);
                      }
                    }}
                  >
                    <span
                      className={cx(classes.railSubPosition, {
                        [classes.railSubPositionActive]: isActiveSub,
                      })}
                    >
                      {topic.position}.{i + 1}
                    </span>
                    <span style={{ flex: 1 }}>{s.title}</span>
                    {showDrift && s.drift?.is_stale && (
                      <span
                        className={classes.railSubStaleDot}
                        title={t("modeling_documentation.rail_sub_stale")}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

TopicsRail.propTypes = {
  topics: PropTypes.array.isRequired,
  currentTopicSlug: PropTypes.string,
  scrolledSubSlug: PropTypes.string,
  query: PropTypes.string,
  onSelectTopic: PropTypes.func.isRequired,
  onSelectSub: PropTypes.func.isRequired,
  onSelectTopicAndSub: PropTypes.func.isRequired,
  showDrift: PropTypes.bool,
};
